import { useId, useState } from 'react';
import {
  ADJUSTMENT_KINDS,
  ADJUSTMENT_KIND_LABEL,
  BILL_REFUSAL,
  adjustmentRefusal,
  billTotals,
  type AdjustmentKind,
  type AdjustmentMode,
} from '@/domain';
import type { PaymentSummary } from '@/application/ports/FinanceRepository';
import { useAddAdjustment } from '@/application/usecases/useFinance';
import { formatAmount } from '@/shared/format/money';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { formRefusal } from '../clients/clientRefusal';
import { useConfirmAction } from '../shell/useConfirmAction';
import { adjustmentInWords, parseAmount } from './moneyForm';
import styles from './Finance.module.css';

const t = strings.finance;

const FIELDS = ['kind', 'mode', 'value', 'reason'] as const;
type Field = (typeof FIELDS)[number];

/**
 * A discount on one case, or a charge added to it. The request of
 * 8 October 2026 (fourth).
 *
 * Two choices and a figure: a discount or a charge, as an amount or as a
 * percentage of the case's lines, and why, which is required because whoever
 * reads the bill next was not there. What the total due becomes is shown as it
 * is typed, worked out by the rule the server uses. A discount that would take
 * the bill below what has been paid is refused before it is sent, in the
 * server's words. Asked about first, with the total before and after.
 */
export function AdjustmentSheet({ summary, onClose }: { summary: PaymentSummary; onClose: () => void }) {
  const add = useAddAdjustment();
  const ask = useConfirmAction();
  const ids = { value: useId(), reason: useId(), kind: useId(), mode: useId() };
  const jobOrderNo = summary.jobOrderNo;

  const [kind, setKind] = useState<AdjustmentKind>('discount');
  const [mode, setMode] = useState<AdjustmentMode>('amount');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [message, setMessage] = useState<string | null>(null);

  const figure = parseAmount(value);
  // What the case would owe with this on it, by the one rule.
  const after =
    figure === null || figure <= 0
      ? null
      : billTotals(
          summary.subtotal,
          [...summary.adjustments, { kind, mode, value: figure }],
          [summary.paidAmount],
          false,
        );

  function clear(field: Field) {
    setMessage(null);
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function refused(failure: unknown): void {
    const refusal = formRefusal(failure, FIELDS);
    if (refusal === null) throw failure;
    setErrors(refusal.fields);
    setMessage(refusal.message);
  }

  function submit() {
    const wrong = adjustmentRefusal({ kind, mode, value: figure, reason });
    setMessage(null);
    if (wrong !== null) {
      setErrors({ [wrong.field]: wrong.message });
      return;
    }
    setErrors({});

    const sum = figure as number;
    const becomes = after as NonNullable<typeof after>;
    if (becomes.paid > 0 && becomes.due < becomes.paid) {
      setMessage(BILL_REFUSAL.belowPaid);
      return;
    }

    ask(
      strings.confirm.addAdjustment(
        jobOrderNo,
        adjustmentInWords(kind, mode, sum, formatAmount),
        formatAmount(summary.due),
        formatAmount(becomes.due),
      ),
      () =>
        add
          .mutateAsync({ jobOrderNo, input: { kind, mode, value: sum, reason: reason.trim() } })
          .then((saved) => {
            toast(saved.status === 'paid' ? t.nowPaid(jobOrderNo) : t.adjustmentAdded, { tone: 'success' });
            onClose();
          }, refused),
      { title: t.adjustmentTitle(jobOrderNo), confirmLabel: t.saveAdjustment },
    );
  }

  return (
    <BottomSheet
      title={t.adjustmentTitle(jobOrderNo)}
      lead={t.adjustmentLead}
      closeLabel={strings.sheet.close}
      onClose={onClose}
      unsaved={
        (value !== '' || reason !== '') && !add.isPending
          ? { question: t.discard, keep: strings.sheet.keep, discard: strings.sheet.discard }
          : null
      }
      footer={
        <>
          {message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          <div className={sheetStyles.actions}>
            <Button onClick={onClose} disabled={add.isPending}>
              {t.cancel}
            </Button>
            <Button variant="primary" className={sheetStyles.primary} loading={add.isPending} onClick={submit}>
              {t.saveAdjustment}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.form}>
        <div className={modalStyles.field}>
          <span className={modalStyles.label} id={ids.kind}>
            {t.kind}
          </span>
          <div className={styles.choices} role="group" aria-labelledby={ids.kind}>
            {ADJUSTMENT_KINDS.map((each) => (
              <button
                key={each}
                type="button"
                aria-pressed={kind === each}
                onClick={() => {
                  setKind(each);
                  clear('kind');
                }}
              >
                {ADJUSTMENT_KIND_LABEL[each]}
              </button>
            ))}
          </div>
        </div>

        <div className={modalStyles.field}>
          <span className={modalStyles.label} id={ids.mode}>
            {t.figureMode}
          </span>
          <div className={styles.choices} role="group" aria-labelledby={ids.mode}>
            <button
              type="button"
              aria-pressed={mode === 'amount'}
              onClick={() => {
                setMode('amount');
                clear('mode');
              }}
            >
              {t.asAmount}
            </button>
            <button
              type="button"
              aria-pressed={mode === 'percent'}
              onClick={() => {
                setMode('percent');
                clear('mode');
              }}
            >
              {t.asPercent}
            </button>
          </div>
        </div>

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.value}>
            {mode === 'percent' ? t.asPercent : t.asAmount}
          </label>
          <input
            id={ids.value}
            className={modalStyles.input}
            type="number"
            inputMode="decimal"
            min={0}
            max={mode === 'percent' ? 100 : undefined}
            step="0.01"
            value={value}
            aria-required="true"
            aria-invalid={errors.value !== undefined || undefined}
            aria-describedby={errors.value === undefined ? undefined : `${ids.value}-error`}
            data-autofocus
            onChange={(event) => {
              setValue(event.target.value);
              clear('value');
            }}
          />
          {errors.value !== undefined && (
            <p id={`${ids.value}-error`} className={modalStyles.error}>
              {errors.value}
            </p>
          )}
        </div>

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.reason}>
            {t.reason}
          </label>
          <input
            id={ids.reason}
            className={modalStyles.input}
            value={reason}
            autoComplete="off"
            placeholder={t.reasonPlaceholder}
            aria-required="true"
            aria-invalid={errors.reason !== undefined || undefined}
            aria-describedby={errors.reason === undefined ? undefined : `${ids.reason}-error`}
            onChange={(event) => {
              setReason(event.target.value);
              clear('reason');
            }}
          />
          {errors.reason !== undefined && (
            <p id={`${ids.reason}-error`} className={modalStyles.error}>
              {errors.reason}
            </p>
          )}
        </div>

        {after !== null && (
          <p className={styles.preview} data-testid="adjustment-preview">
            {t.preview(formatAmount(summary.due), formatAmount(after.due))}
          </p>
        )}
      </div>
    </BottomSheet>
  );
}
